import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Hammer, Zap, Wrench, Brush, Home, Building2, Briefcase, HardHat,
  FileText, FileSignature, FileCheck2, Receipt, Sparkles, Check,
  ArrowRight, ClipboardList, ChevronRight,
} from "lucide-react";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";

const trades = [
  { icon: HardHat, label: "General Contractors" },
  { icon: Zap, label: "Electricians" },
  { icon: Wrench, label: "Plumbers" },
  { icon: Hammer, label: "Masons" },
  { icon: Brush, label: "Painters" },
  { icon: Briefcase, label: "Handymen" },
  { icon: Home, label: "Landlords" },
  { icon: Building2, label: "Property Managers" },
];

const features = [
  { icon: Sparkles, title: "AI quote writer", desc: "Polished contractor language from messy notes." },
  { icon: Receipt, title: "Invoice mode", desc: "Same engine, formatted as an invoice." },
  { icon: FileText, title: "Estimate mode", desc: "Customer-ready estimates in seconds." },
  { icon: FileSignature, title: "Change order mode", desc: "Quickly document scope additions." },
  { icon: ClipboardList, title: "Itemized breakdowns", desc: "Line items that justify your price." },
  { icon: FileCheck2, title: "PDF export", desc: "Clean, branded documents to send." },
];

const plans = [
  {
    id: "starter", name: "Starter", price: 19,
    features: ["20 quotes per month", "Estimate generator", "Copy text output", "Basic templates"],
  },
  {
    id: "pro", name: "Pro", price: 49, badge: "Best Value",
    features: ["Unlimited quotes", "PDF export", "Saved company info", "Saved clients", "Estimate, invoice & change order modes"],
  },
  {
    id: "crew", name: "Crew", price: 99,
    features: ["Everything in Pro", "Multiple users", "Saved trade templates", "Custom disclaimers", "Priority support"],
  },
];

function NavBar() {
  return (
    <header className="sticky top-0 z-40 w-full bg-white/85 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="h-8 w-8 rounded-lg bg-orange-500 flex items-center justify-center text-white">
            <Hammer className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-bold text-slate-900">ScopeWriter <span className="text-orange-500">AI</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
          <a href="#features" className="hover:text-slate-900">Features</a>
          <a href="#how" className="hover:text-slate-900">How it works</a>
          <a href="#pricing" className="hover:text-slate-900">Pricing</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/auth" data-testid="nav-login-link">
            <Button variant="ghost" className="text-slate-700">Log in</Button>
          </Link>
          <Link to="/auth?mode=signup" data-testid="nav-signup-link">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">Create my first quote</Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function BeforeAfterCard() {
  return (
    <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-testid="before-after-card">
      <Card className="p-5 bg-slate-900 text-slate-100 border-slate-800 shadow-card">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Before — your notes</div>
        <p className="font-mono text-sm leading-relaxed text-slate-200">
          add 2 outside lights side of building, run wire, install boxes, 1350
        </p>
      </Card>
      <Card className="p-5 bg-white border-orange-200 shadow-card relative">
        <div className="absolute -top-3 left-4">
          <Badge className="bg-orange-500 hover:bg-orange-500 text-white">ScopeWriter AI</Badge>
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">After — customer-ready</div>
        <p className="text-sm leading-relaxed text-slate-700">
          Run necessary exterior wiring for two new light locations on the side of the building. Install electrical backboxes as needed, mount and connect two exterior light fixtures, and test for proper operation upon completion.
        </p>
      </Card>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <NavBar />

      {/* HERO */}
      <section className="relative bg-grid">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="max-w-3xl">
            <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700 mb-5">
              <Sparkles className="h-3 w-3 mr-1" /> Built for real contractors
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Professional contractor quotes from <span className="text-orange-500">messy job notes.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Paste rough job notes, prices, and project details. ScopeWriter AI turns them into clean, customer-ready estimates, invoices, and scope-of-work PDFs — in seconds.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/auth?mode=signup" data-testid="hero-cta-create">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 text-base">
                  Create My First Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#example" data-testid="hero-cta-example">
                <Button size="lg" variant="outline" className="h-12 px-7 text-base border-slate-300">
                  View Example
                </Button>
              </a>
            </div>
          </div>

          <div id="example" className="mt-14">
            <BeforeAfterCard />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-12">
            <div className="text-sm font-medium text-orange-600 tracking-wide uppercase mb-2">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Three steps. No fluff.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Paste your rough notes", d: "Add job details, pricing, and customer info — exactly as you'd jot them on a notepad." },
              { n: "02", t: "Generate a polished scope", d: "AI rewrites the job into professional contractor language that justifies your price." },
              { n: "03", t: "Send a clean quote", d: "Export to PDF or copy the quote to send by email or text — looks like you have an office team." },
            ].map((s) => (
              <Card key={s.n} className="p-7 border border-slate-200 shadow-card hover:shadow-card-hover transition-shadow">
                <div className="text-orange-500 font-display text-3xl font-bold mb-3">{s.n}</div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{s.t}</h3>
                <p className="text-slate-600 leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-12">
            <div className="text-sm font-medium text-orange-600 tracking-wide uppercase mb-2">Who it's for</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">From one-truck operators to property managers.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trades.map((t) => (
              <Card key={t.label} className="p-5 flex items-center gap-3 border border-slate-200 shadow-card hover:border-orange-200 hover:shadow-card-hover transition-all">
                <div className="h-10 w-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                  <t.icon className="h-5 w-5" />
                </div>
                <span className="font-medium text-slate-800 text-sm">{t.label}</span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-12">
            <div className="text-sm font-medium text-orange-600 tracking-wide uppercase mb-2">Features</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Everything you need. Nothing you don't.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <Card key={f.title} className="p-6 border border-slate-200 shadow-card">
                <div className="h-10 w-10 rounded-lg bg-slate-900 text-orange-400 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-12 text-center">
            <div className="text-sm font-medium text-orange-600 tracking-wide uppercase mb-2">Pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Simple plans. Cancel anytime.</h2>
            <p className="mt-3 text-slate-600">Or pay $5 per quote — no subscription needed.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => {
              const isPro = p.name === "Pro";
              return (
                <Card
                  key={p.name}
                  data-testid={`pricing-card-${p.id}`}
                  className={`p-7 border shadow-card relative ${isPro ? "border-orange-300 ring-2 ring-orange-200" : "border-slate-200"}`}
                >
                  {p.badge && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 hover:bg-orange-500 text-white">
                      {p.badge}
                    </Badge>
                  )}
                  <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-display text-4xl font-bold text-slate-900">${p.price}</span>
                    <span className="text-slate-500">/month</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/auth?mode=signup" data-testid={`pricing-cta-${p.id}`}>
                    <Button
                      onClick={(e) => {
                        if (user) {
                          e.preventDefault();
                          startCheckout(p.id);
                        }
                      }}
                      className={`mt-7 w-full ${isPro ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
                    >
                      Get started <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight">
            Stop sending ugly estimates. <br />
            <span className="text-orange-400">Start sending quotes that look professional.</span>
          </h2>
          <div className="mt-8">
            <Link to="/auth?mode=signup" data-testid="final-cta">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-8 text-base">
                Create My First Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-8 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-sm text-slate-500 flex flex-col sm:flex-row gap-2 justify-between">
          <div>© {new Date().getFullYear()} ScopeWriter AI. Built for contractors.</div>
          <div className="flex gap-5">
            <a href="#pricing" className="hover:text-slate-800">Pricing</a>
            <a href="#features" className="hover:text-slate-800">Features</a>
            <Link to="/auth" className="hover:text-slate-800">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
